import { FC, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Modal, ModalFooter, Button, Input } from '@/components/ui';
import { IndustryAutocomplete } from '@/components/wizard/IndustryAutocomplete';
import { targetSchema, type TargetFormData } from '@/validations';
import { Target } from '../types';

interface TargetModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (target: Target) => void;
  target?: Target;
  mode: 'create' | 'edit';
}

/**
 * Target Creation/Edit Modal
 * Uses react-hook-form with generic form components (like wizard steps)
 */
export const TargetModal: FC<TargetModalProps> = ({
  isOpen,
  onClose,
  onSave,
  target,
  mode,
}) => {
  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
    reset,
  } = useForm<TargetFormData>({
    resolver: zodResolver(targetSchema),
    defaultValues: {
      name: target?.name || '',
      role: target?.role || '',
      industry: target?.industry || '',
      country: target?.country || '',
      language: target?.language || '',
    },
  });

  // Reset form when target changes
  useEffect(() => {
    if (isOpen) {
      reset({
        name: target?.name || '',
        role: target?.role || '',
        industry: target?.industry || '',
        country: target?.country || '',
        language: target?.language || '',
      });
    }
  }, [target, isOpen, reset]);

  const onSubmit = (data: TargetFormData) => {
    const targetData: Target = {
      ...target, // Preserve id if editing
      name: data.name,
      role: data.role,
      industry: data.industry,
      country: data.country,
      language: data.language,
    };
    onSave(targetData);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={mode === 'create' ? 'Create Target' : 'Edit Target'}
      size="md"
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        {/* Name */}
        <Controller
          name="name"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              type="text"
              label="Name"
              placeholder="e.g. Enterprise Sales Target"
              fullWidth
              error={errors.name?.message}
            />
          )}
        />

        {/* Role */}
        <Controller
          name="role"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              type="text"
              label="Role"
              placeholder="e.g. Marketing Manager"
              fullWidth
              optional
              error={errors.role?.message}
            />
          )}
        />

        {/* Industry - Autocomplete */}
        <Controller
          name="industry"
          control={control}
          render={({ field }) => (
            <IndustryAutocomplete
              value={field.value || ''}
              onChange={(value) => setValue('industry', value)}
              error={errors.industry?.message}
              required
            />
          )}
        />

        {/* Country */}
        <Controller
          name="country"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              type="text"
              label="Country"
              placeholder="e.g. US"
              fullWidth
              optional
              error={errors.country?.message}
            />
          )}
        />

        {/* Language */}
        <Controller
          name="language"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              type="text"
              label="Language"
              placeholder="e.g. English"
              fullWidth
              optional
              error={errors.language?.message}
            />
          )}
        />

        <ModalFooter>
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" variant="primary">
            Save Target
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
};
